<template>
    <PanelFrame ref="panelFrame" panel-class="filemanager-app" resize-directions="all" :content-class="panelContentClass">
        <template #titlebar-left>
            <button v-if="isCompact && mobileView !== 'main'" class="panel-titlebar-btn" @click.stop="mobileView = 'main'">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button v-else class="panel-titlebar-btn" @click.stop="isCompact ? (mobileView = 'sidebar') : (sidebarOpen = !sidebarOpen)" v-tooltip="$t('common.fileManager.sidebar')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span class="fm-titlebar-label">{{ pickerMode ? $t('common.fileManager.titlePicker') : $t('common.fileManager.title') }}</span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="onRefresh" v-tooltip="$t('common.fileManager.refresh')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
        </template>

        <template #default>
            <div class="fm-body"
                 @dragenter.prevent="onBodyDragEnter"
                 @dragleave.prevent="onBodyDragLeave"
                 @dragover.prevent="onBodyDragOver"
                 @drop.prevent="onBodyDrop">
                <FileManagerSidebar ref="sidebar"
                    v-if="showSidebar"
                    :width="isCompact ? null : sidebarWidth"
                    :picker-mode="pickerMode"
                    @select-category="onSelectCategory"
                    @navigate-folder="onNavigateFolder"
                    @folder-context="onSidebarFolderContext"
                    @folder-reorder="onFolderReorder"
                    @folder-drop="onFolderDrop"
                    @rename-folder="onRenameSidebarFolder"
                    @trash-drop="onTrashDrop"
                    @category-drop="onCategoryDrop"
                    @depth-limit="onSidebarDepthLimit"
                    @divider-start="startSidebarResize"/>

                <div v-if="!isCompact && sidebarOpen" class="panel-divider"
                     @mousedown="startSidebarResize"
                     @touchstart.prevent="startSidebarResize"></div>

                <FileManagerContent ref="fileContent"
                    v-show="showContent"
                    :picker-mode="pickerMode"
                    :picker-accept="pickerAccept"
                    :picker-multiple="pickerMultiple"
                    :picker-entity-folder="isPickerEntityFolder"
                    :picker-can-detach="pickerCanDetach"
                    :picker-can-delete="pickerCanDelete"
                    @context-menu="onContextMenu"
                    @close-context-menu="$refs.contextMenu.close(true)"
                    @upload="onUploadClick"
                    @picker-cancel="closePicker"
                    @picker-select="onPickerSelect"
                    @picker-detach="onPickerDetach"
                    @picker-delete="onPickerDelete"
                    @preview="onPreviewFile"
                    @drag-upload="onDragUpload"
                    @delete-items="onDelete"
                    @permanent-delete="onPermanentDelete"
                    @lightbox="onLightbox"
                    @depth-limit="onContentDepthLimit"
                    @restore-all="onRestoreAll"
                    @empty-trash="onEmptyTrash"
                    @download-selected="onDownloadSelected"
                    @move-selected="onMove"/>

                <Transition name="fm-preview">
                    <FileManagerPreview
                        v-if="showPreview"
                        ref="previewPanel"
                        :file="fm.state.previewFile"
                        @close="fm.setPreviewFile(null); if (isCompact) mobileView = 'main'"
                        @download="onDownload"
                        @lightbox="onLightbox"/>
                </Transition>

                <FileManagerUploadOverlay ref="uploadOverlay"
                    :visible="showUploadOverlay || isDragUpload"
                    :manual-mode="showUploadOverlay && !isDragUpload"
                    :upload-module="uploadModule"
                    :upload-entity-id="uploadEntityId"
                    @close="closeUploadOverlay"/>

                <div v-if="showVwoPicker" class="fm-vwo-picker-overlay" @click="showVwoPicker = false">
                    <div class="fm-vwo-picker" @click.stop>
                        <div class="fm-vwo-picker-title">{{ $t('common.fileManager.whichWorkOrder') }}</div>
                        <div class="fm-vwo-picker-list">
                            <button v-for="id in vwoEntityIds" :key="id" class="fm-vwo-picker-item" @click="onVwoSelect(id)">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18.4 7.6c-.3-.8-1.1-1.6-2-1.6H7.6c-.9 0-1.7.8-2 1.6L3.5 11.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>
                                {{ $t('common.fileManager.workOrderPrefix') }}{{ id }}
                            </button>
                        </div>
                        <button class="fm-vwo-picker-cancel" @click="showVwoPicker = false">{{ $t('common.fileManager.cancel') }}</button>
                    </div>
                </div>

                <FileManagerUploadProgress/>
            </div>
        </template>

        <template #overlays>
            <FileManagerContextMenu ref="contextMenu"
                :z-base="panelZBase"
                :active-category="fm.state.activeCategory"
                :picker-mode="pickerMode"
                :picker-entity-folder="isPickerEntityFolder"
                :picker-can-delete="pickerCanDelete"
                :picker-can-detach="pickerCanDetach"
                @rename="onRename"
                @delete="onDelete"
                @restore="onRestore"
                @permanent-delete="onPermanentDelete"
                @move="onMove"
                @preview="onPreviewFromCtx"
                @download="onDownload"
                @open="onOpenFolder"
                @new-folder="onNewFolder"
                @upload="onUploadClick"
                @select-all="fm.selectAll()"
                @navigate-entity="onNavigateEntity"
                @new-sub-folder="onNewSubFolder"
                @restore-all="onRestoreAll"
                @empty-trash="onEmptyTrash"
                @detach="onDetach"/>

            <FileManagerLightbox
                :file="lightboxFile"
                @close="lightboxFile = null"
                @navigate="lightboxFile = $event"/>
        </template>
    </PanelFrame>
</template>

<script>
import './filemanager-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import FileManagerSidebar from './FileManagerSidebar.vue'
import FileManagerContent from './FileManagerContent.vue'
import FileManagerPreview from './FileManagerPreview.vue'
import FileManagerContextMenu from './FileManagerContextMenu.vue'
import FileManagerUploadOverlay from './FileManagerUploadOverlay.vue'
import FileManagerUploadProgress from './FileManagerUploadProgress.vue'
import FileManagerLightbox from './FileManagerLightbox.vue'
import fileManagerService from './fileManagerService'
import fileManagerPicker from './fileManagerPicker'

const fileManagerPanelMixin = createPanelMixin({
    panelId: 'fileManagerPanel',
    shortcut: { code: 'KeyF' },
    minWidth: 500,
    minHeight: 400,
    defaults: {
        floatingMode: false,
        width: 1100,
        height: 700,
        sidebarWidth: 250,
    },
    hasInnerLayout: true,
    showSettingsInCompact: true,
    minSidebar: 160,
    maxSidebar: 300,
    settingsItems: [
        'alwaysOnTop',
        'openOnLoad',
        'divider',
        'transparent',
        'divider',
        'resetWindowSize',
        'resetInnerLayout',
    ],
    onOpen(vm) {
        if (vm.pickerMode) {
            vm.pickerMode = false
            fileManagerService.state.activeCategory = 'ALL'
            fileManagerService.state.currentFolderId = null
        }
        fileManagerService.init()
    },
    onClose(vm) {
        if (vm.pickerMode) vm.closePicker(true)
        vm.lightboxFile = null
    },
    onEscape(vm) {
        if (vm.$refs.previewPanel?.showPdfViewer) {
            vm.$refs.previewPanel.closePdfViewer()
            return true
        }
        if (vm.lightboxFile) {
            vm.lightboxFile = null
            return true
        }
        if (vm.showVwoPicker) {
            vm.showVwoPicker = false
            return true
        }
        if (vm.$refs.contextMenu?.visible) {
            vm.$refs.contextMenu.close()
            return true
        }
        if (vm.showUploadOverlay) {
            vm.closeUploadOverlay()
            return true
        }
        if (vm.fm.state.previewFile && !vm.isCompact) {
            vm.fm.setPreviewFile(null)
            return true
        }
        if (vm.isCompact && vm.mobileView !== 'main') {
            vm.mobileView = 'main'
            return true
        }
        if (vm.pickerMode) {
            vm.closePicker()
            return true
        }
        return false
    },
    async onContext(vm, ctx) {
        if (ctx.pickerMode) vm.openPicker(ctx)
        if (ctx.uploadable_type && ctx.uploadable_id) {
            await fileManagerService.navigateToEntityDirect(ctx.uploadable_type, ctx.uploadable_id)
            if (ctx.pickerMode) {
                const entityFolderId = 'entity_' + ctx.uploadable_type + '_' + ctx.uploadable_id
                const folder = fileManagerService.state.folders.find(f => f.id === entityFolderId)
                if (folder) fileManagerService.state._pickerEntity = folder
            }
        } else if (ctx.folderId != null) {
            fileManagerService.navigateToFolder(ctx.folderId)
        }
        if (ctx.previewFileId != null) {
            fileManagerService.setCategory('ALL')
            fileManagerService.fetchFileById(ctx.previewFileId).then(file => {
                if (file) fileManagerService.setPreviewFile(file)
            })
        }
    },
    extraStorageKeys(vm) {
        return { sidebarOpen: vm.sidebarOpen, viewMode: fileManagerService.state.viewMode, autoPreview: fileManagerService.state.autoPreview, dockBadge: fileManagerService.state.dockBadge }
    },
})

export default {
    name: 'FileManagerPanel',
    mixins: [fileManagerPanelMixin],
    components: { PanelFrame, FileManagerSidebar, FileManagerContent, FileManagerPreview, FileManagerContextMenu, FileManagerUploadOverlay, FileManagerUploadProgress, FileManagerLightbox },
    data() {
        const saved = this.panelLoadLayout()
        return {
            sidebarOpen: saved.sidebarOpen ?? true,
            mobileView: 'main',
            showUploadOverlay: false,
            isDragUpload: false,
            dragEnterCount: 0,
            lightboxFile: null,
            showVwoPicker: false,
            uploadModule: null,
            uploadEntityId: null,

            pickerMode: false,
            pickerAccept: null,
            pickerMultiple: true,
            pickerCallback: null,
            pickerUploadType: null,
            pickerUploadId: null,
            pickerCanDelete: false,
            pickerCanDetach: false,
        }
    },
    computed: {
        fm() { return fileManagerService },
        panelContentClass() {
            return 'fm-active'
        },
        showSidebar() {
            if (this.isCompact) return this.mobileView === 'sidebar'
            return this.sidebarOpen
        },
        showContent() {
            if (this.isCompact) return this.mobileView === 'main'
            return true
        },
        vwoEntityIds() {
            return fileManagerService.getModuleEntityIds('MODULE_VEHICLE_WORK_ORDER')
        },
        showPreview() {
            if (this.pickerMode) return false
            if (this.isCompact) return this.mobileView === 'preview' && this.fm.state.previewFile
            return this.fm.state.previewFile !== null
        },
        isPickerEntityFolder() {
            if (!this.pickerMode || !this.pickerUploadType || !this.pickerUploadId) return false
            return this.fm.state.currentFolderId === 'entity_' + this.pickerUploadType + '_' + this.pickerUploadId
        },
    },
    methods: {
        onSelectCategory(cat) {
            fileManagerService.setCategory(cat)
            if (this.isCompact) this.mobileView = 'main'
        },
        onNavigateFolder(folderId) {
            fileManagerService.navigateToFolder(folderId)
            if (this.isCompact) this.mobileView = 'main'
        },
        onContextMenu({ event, item, items }) {
            this.$refs.contextMenu.openAt(event, item, items)
        },
        onSidebarFolderContext({ event, folder }) {
            const item = { id: folder.id, uid: folder.uid, name: folder.name, type: 'folder', color: folder.color }
            this.$refs.contextMenu.openAt(event, item, [], 'sidebar')
        },
        onRename({ item, source }) {
            if (source === 'sidebar' && item.type === 'folder' && this.$refs.sidebar) {
                this.$refs.sidebar.startRenameFolder(item)
            } else {
                this.$refs.fileContent?.startRename(item.uid, item.name)
            }
        },
        onRenameSidebarFolder({ uid, name }) {
            fileManagerService.renameItemByUid(uid, name)
        },
        onTrashDrop(ids) {
            fileManagerService.deleteItems(ids)
        },
        onCategoryDrop({ ids, category }) {
            if (category === 'ALL') {
                fileManagerService.moveItems(ids, null)
            } else if (category === 'UNATTACHED') {
                fileManagerService.moveItems(ids, null)
            }
        },
        onDelete(ids) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.deleteConfirmTitle'),
                message: ids.length === 1 ? this.$t('common.fileManager.deleteConfirmSingle') : this.$t('common.fileManager.deleteConfirmMultiple', {count: ids.length}),
                confirmText: this.$t('common.fileManager.delete'),
                cancelText: this.$t('common.fileManager.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    fileManagerService.deleteItems(ids)
                },
            })
        },
        onRestore(ids) {
            fileManagerService.restoreItems(ids)
        },
        onPermanentDelete(ids) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.permanentDeleteTitle'),
                message: ids.length === 1
                    ? this.$t('common.fileManager.permanentDeleteSingle')
                    : this.$t('common.fileManager.permanentDeleteMultiple', {count: ids.length}),
                confirmText: this.$t('common.fileManager.permanentDelete'),
                cancelText: this.$t('common.fileManager.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    fileManagerService.permanentDeleteItems(ids)
                },
            })
        },
        onDetach(uids) {
            const files = fileManagerService.state.files.filter(f => uids.includes(f.uid))
            if (!files.length) return

            let uploadableType = this.pickerUploadType
            let uploadableId = this.pickerUploadId
            if (!uploadableType || !uploadableId) {
                const folder = fileManagerService.state.folders.find(f => f.id === fileManagerService.state.currentFolderId)
                if (!folder?.entityId || !folder?.uploadableType) return
                uploadableType = folder.uploadableType
                uploadableId = folder.entityId
            }

            const count = files.length
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.detachConfirmTitle'),
                message: count === 1 ? this.$t('common.fileManager.detachConfirmSingle') : this.$t('common.fileManager.detachConfirmMultiple', {count}),
                confirmText: this.$t('common.fileManager.detach'),
                cancelText: this.$t('common.fileManager.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    const uploadIds = files.map(f => f.id)
                    this.axios.post(this.endpoints.upload_detach_batch, {
                        uploadable_type: uploadableType,
                        uploadable_id: uploadableId,
                        upload_ids: uploadIds,
                    }).then(() => {
                        const detachedFiles = fileManagerService.state.files.filter(f => uids.includes(f.uid))
                        fileManagerService.state.files = fileManagerService.state.files.filter(f => !uids.includes(f.uid))
                        fileManagerService.clearSelection()
                        fileManagerService.refreshCategories()
                        fileManagerService.loadUnattachedCount()
                        this._firePickerEvent('onDetach', detachedFiles)
                    }).catch(() => {
                        fileManagerService.state.lastError = 'errorDetach'
                    })
                },
            })
        },
        onPickerDetach() {
            const uids = [...fileManagerService.state.selectedIds]
            if (uids.length) this.onDetach(uids)
        },
        onPickerDelete() {
            const uids = [...fileManagerService.state.selectedIds]
            if (uids.length) this.onDelete(uids)
        },
        onRestoreAll() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.restoreAll'),
                message: this.$t('common.fileManager.restoreAllConfirm'),
                confirmText: this.$t('common.fileManager.restore'),
                cancelText: this.$t('common.fileManager.cancel'),
                confirmType: 'primary',
                onConfirm: () => {
                    fileManagerService.restoreAllTrash()
                },
            })
        },
        onEmptyTrash() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.emptyTrash'),
                message: this.$t('common.fileManager.emptyTrashConfirm'),
                confirmText: this.$t('common.fileManager.continue'),
                cancelText: this.$t('common.fileManager.cancel'),
                confirmType: 'danger',
                onConfirm: () => {
                    fileManagerService.emptyTrash()
                },
            })
        },
        onMove({ ids, folderId }) {
            if (folderId) {
                const hasFolders = ids.some(uid => fileManagerService.state.folders.find(f => f.uid === uid))
                if (hasFolders && !fileManagerService.canCreateSubfolder(folderId)) {
                    this.eventBus.$emit('showConfirmDialog', {
                        title: this.$t('common.fileManager.depthLimitTitle'),
                        message: this.$t('common.fileManager.depthLimitMove'),
                        singleButton: true,
                        confirmType: 'default',
                        confirmText: this.$t('common.ok'),
                    })
                    return
                }
            }
            fileManagerService.moveItems(ids, folderId)
        },
        onFolderReorder(updates) {
            fileManagerService.reorderFolders(updates)
        },
        onFolderDrop({ folderId, parentId }) {
            fileManagerService.moveFolderToParent(folderId, parentId)
        },
        onPreviewFile(file) {
            if (!fileManagerService.state.autoPreview) return
            fileManagerService.setPreviewFile(file)
            if (this.isCompact) this.mobileView = 'preview'
        },
        onPreviewFromCtx(item) {
            fileManagerService.setPreviewFile(item)
            if (this.isCompact) this.mobileView = 'preview'
        },
        onOpenFolder(item) {
            if (item.type === 'folder') {
                fileManagerService.navigateToFolder(item.id)
            }
        },
        onNewFolder() {
            this.$refs.fileContent?.onNewFolder()
        },
        onNewSubFolder(item) {
            if (!fileManagerService.canCreateSubfolder(item.id)) {
                const parentFolder = fileManagerService.state.folders.find(f => f.id === item.id)
                const upperParentId = parentFolder?.parentId || null
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('common.fileManager.depthLimitTitle'),
                    message: this.$t('common.fileManager.depthLimitParent'),
                    confirmText: this.$t('common.fileManager.addToParent'),
                    cancelText: this.$t('common.fileManager.cancel'),
                    confirmType: 'primary',
                    onConfirm: () => {
                        if (upperParentId) {
                            this.$refs.sidebar?.startNewSubFolder(upperParentId)
                        } else {
                            this.$refs.sidebar?.startNewRootFolder()
                        }
                    },
                })
                return
            }
            this.$refs.sidebar?.startNewSubFolder(item.id)
        },
        onSidebarDepthLimit(type) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.depthLimitTitle'),
                message: this.$t('common.fileManager.depthLimitMove'),
                singleButton: true,
                confirmType: 'default',
                confirmText: this.$t('common.ok'),
            })
        },
        onContentDepthLimit() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.depthLimitTitle'),
                message: this.$t('common.fileManager.depthLimitMessage'),
                singleButton: true,
                confirmType: 'default',
                confirmText: this.$t('common.ok'),
            })
        },
        onNavigateEntity(info) {
            if (info) fileManagerService.navigateEntity(info.type, info.id, this.$router)
        },
        onDownload(file) {
            if (file?.publicUrl && file.publicUrl !== '#') {
                const url = file.publicUrl + (file.publicUrl.includes('?') ? '&' : '?') + 'download=1'
                this.trustedWindowOpen(url, null, file.name)
            }
        },
        onDownloadSelected() {
            const items = this.$refs.fileContent?.getSelectedItems() || []
            const files = items.filter(i => i.type === 'file' && i.publicUrl && i.publicUrl !== '#')
            files.forEach(f => this.onDownload(f))
        },
        onRefresh() {
            fileManagerService.refresh()
        },
        onUploadClick() {
            if (this.pickerMode && this.pickerUploadType && this.pickerUploadId) {
                this.uploadModule = this.pickerUploadType
                this.uploadEntityId = this.pickerUploadId
                this.showUploadOverlay = true
                return
            }
            const cat = fileManagerService.state.activeCategory
            if (cat === 'MODULE_VEHICLE_WORK_ORDER') {
                this.showVwoPicker = true
                return
            }
            this.uploadModule = null
            this.uploadEntityId = null
            this.showUploadOverlay = true
        },
        onVwoSelect(entityId) {
            this.showVwoPicker = false
            this.uploadModule = 'MODULE_VEHICLE_WORK_ORDER'
            this.uploadEntityId = entityId
            this.showUploadOverlay = true
        },
        closeUploadOverlay() {
            this.showUploadOverlay = false
            this.isDragUpload = false
            this.dragEnterCount = 0
            this.uploadModule = null
            this.uploadEntityId = null
        },
        onLightbox(file) {
            this.lightboxFile = file
        },
        onDragUpload(active) {
            this.isDragUpload = active
        },
        _isInternalDrag(e) {
            if ('ontouchstart' in window) return true
            const t = e.dataTransfer.types
            return t.includes('application/fm-items') || t.includes('fm-folder-id')
        },
        _canUploadInCurrentFolder() {
            const f = fileManagerService.state.currentFolderId
                ? fileManagerService.state.folders.find(fl => fl.id === fileManagerService.state.currentFolderId)
                : null
            if (!f) return true
            if (f.moduleType) return false
            if (f.entityId) return f.directUpload !== false
            return true
        },
        onBodyDragEnter(e) {
            if (this._isInternalDrag(e)) return
            if (this.pickerMode && !this.isPickerEntityFolder) return
            if (!this._canUploadInCurrentFolder()) return
            this.dragEnterCount++
            if (this.dragEnterCount === 1) this.isDragUpload = true
        },
        onBodyDragLeave(e) {
            if (this._isInternalDrag(e)) return
            this.dragEnterCount--
            if (this.dragEnterCount <= 0) {
                this.dragEnterCount = 0
                this.isDragUpload = false
            }
        },
        onBodyDragOver(e) {
            if (this._isInternalDrag(e)) return
            e.dataTransfer.dropEffect = 'copy'
        },
        onBodyDrop(e) {
            if (this._isInternalDrag(e)) return
            if (this.pickerMode && !this.isPickerEntityFolder) return
            if (!this._canUploadInCurrentFolder()) return
            if (this.showUploadOverlay || this.isDragUpload) return
            this.dragEnterCount = 0
            this.isDragUpload = false
            const files = e.dataTransfer?.files
            if (files && files.length) {
                fileManagerService.uploadFiles(Array.from(files))
            }
        },

        openPicker(ctx) {
            this.pickerMode = true
            this.pickerAccept = ctx.accept || null
            this.pickerMultiple = ctx.multiple !== false
            this.pickerCallback = ctx.onSelect || null
            this.pickerUploadType = ctx.uploadable_type || null
            this.pickerUploadId = ctx.uploadable_id || null
            this.pickerCanDelete = ctx.canDelete === true
            this.pickerCanDetach = ctx.canDetach === true
            if (ctx._pickerCallbacks) {
                fileManagerService.state._pickerEvents = ctx._pickerCallbacks
            }
            fileManagerService.clearSelection()
        },
        closePicker(silent) {
            const currentFiles = [...fileManagerService.state.files]
            this._firePickerEvent('onClose', currentFiles)
            this.pickerCallback = null
            this.pickerAccept = null
            this.pickerUploadType = null
            this.pickerUploadId = null
            this.pickerCanDelete = false
            this.pickerCanDetach = false
            fileManagerService.state._pickerEvents = null
            fileManagerPicker.clearCallbacks()
            fileManagerService.state._pickerEntity = null
            fileManagerService.clearSelection()
            if (silent) {
                return
            }
            this.pickerMode = false
            if (fileManagerService.state.activeCategory !== 'ALL' || fileManagerService.state.currentFolderId) {
                fileManagerService.setCategory('ALL')
            }
        },
        _firePickerEvent(name, data) {
            const cbs = fileManagerService.state._pickerEvents
            if (cbs && typeof cbs[name] === 'function') cbs[name](data)
        },
        async onPickerSelect() {
            const selectedIds = new Set(fileManagerService.state.selectedIds)
            const selectedFiles = fileManagerService.state.files.filter(f => selectedIds.has(f.uid))
            if (this.pickerCallback) {
                this.pickerCallback(selectedFiles)
            } else if (this.pickerUploadType && this.pickerUploadId && selectedFiles.length) {
                try {
                    await fileManagerPicker.attachFiles(this.pickerUploadType, this.pickerUploadId, selectedFiles.map(f => f.id))
                    this._firePickerEvent('onAttach', selectedFiles)
                } catch { /* empty */ }
            }
            this.closePicker()
            this.close()
        },

        onResetInnerLayout() {
            this.sidebarWidth = 250
            this.sidebarOpen = true
            fileManagerService.setPreviewFile(null)
            this.panelSaveLayoutDebounced()
        },
    },
    watch: {
        sidebarOpen() { this.panelSaveLayoutDebounced() },
        'fm.state.viewMode'() { this.panelSaveLayoutDebounced() },
        'fm.state.dockBadge'() { this.panelSaveLayoutDebounced() },
        'fm.state.autoPreview'() { this.panelSaveLayoutDebounced() },
        showPreview() {
            const el = this.$refs.fileContent?.$el
            if (!el) return
            el.classList.add('fm-content-reflow')
            clearTimeout(this._reflowTimer)
            this._reflowTimer = setTimeout(() => el.classList.remove('fm-content-reflow'), 300)
        },
        'fm.state.lastError'(err) {
            if (!err) return
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('common.fileManager.errorTitle'),
                message: this.$t(err),
                singleButton: true,
                confirmType: 'default',
                confirmText: this.$t('common.ok'),
            })
            fileManagerService.state.lastError = null
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.$watch('panelWidth', (w, old) => {
                if (!this.isCompact && w < 700 && old >= 700 && this.sidebarOpen) {
                    this.sidebarOpen = false
                }
            })
        })
    },
}
</script>
